import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation, Trans } from 'react-i18next'
import { pageUrl } from '../seo'
import { Plus, Minus } from 'lucide-react'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

const PAGE_BG = {
    backgroundImage: `url(/assets/images/trafico.jpg)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
}

/* `contexto` acrescenta ao NOME ACESSÍVEL do cabeçalho a secção a que ele pertence,
   sem mudar o texto visível. Há títulos repetidos entre secções («Tráfico de Seres
   Humanos» em duas, «Exploração» em três) e, numa lista de cabeçalhos de leitor de
   ecrã, lidos fora de contexto ficavam indistinguíveis. O texto visível continua a
   ser o prefixo do nome acessível, pelo que o comando por voz continua a funcionar. */
function AccordionDark({ id, title, contexto, children, open, onToggle }) {
    return (
        <div className="overflow-hidden rounded-xl" style={{ backgroundColor: 'rgba(0,32,63,0.88)' }}>
            {/* O título é um heading (navegação por cabeçalhos nos leitores de ecrã);
                o botão vive DENTRO do heading — heading dentro de button é HTML inválido. */}
            <h3>
                <button
                    onClick={onToggle}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left font-bold transition-colors hover:opacity-90 focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-inset"
                    style={{ color: '#ADEFD1', fontFamily: 'Rubik, system-ui, sans-serif' }}
                    aria-expanded={open}
                    /* o painel só existe no DOM quando aberto — um aria-controls a
                       apontar para um id inexistente é um IDREF inválido */
                    aria-controls={open ? id : undefined}
                >
                    <span className="text-base">{title}</span>
                    {contexto && <span className="sr-only"> — {contexto}</span>}
                    <span className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#ADEFD1', color: '#00203F' }}>
                        {open ? <Minus size={14} /> : <Plus size={14} />}
                    </span>
                </button>
            </h3>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key="content"
                        id={id}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        <div className="px-6 pb-6 pt-2 text-white/90 leading-relaxed space-y-3 text-base">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

/* `contexto` acrescenta ao NOME ACESSÍVEL do cabeçalho a secção a que ele pertence,
   sem mudar o texto visível. Há títulos repetidos entre secções («Tráfico de Seres
   Humanos» em duas, «Exploração» em três) e, numa lista de cabeçalhos de leitor de
   ecrã, lidos fora de contexto ficavam indistinguíveis. O texto visível continua a
   ser o prefixo do nome acessível, pelo que o comando por voz continua a funcionar. */
function AccordionLight({ id, title, contexto, children, open, onToggle }) {
    return (
        <div className="overflow-hidden rounded-xl border" style={{ borderColor: 'rgba(0,32,63,0.15)', backgroundColor: 'rgba(255,255,255,0.95)' }}>
            <h3>
                <button
                    onClick={onToggle}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left font-bold transition-colors hover:opacity-90 focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-inset"
                    style={{ color: '#00203F', fontFamily: 'Rubik, system-ui, sans-serif' }}
                    aria-expanded={open}
                    aria-controls={open ? id : undefined}
                >
                    <span className="text-base">{title}</span>
                    {contexto && <span className="sr-only"> — {contexto}</span>}
                    <span className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#00203F', color: '#ADEFD1' }}>
                        {open ? <Minus size={14} /> : <Plus size={14} />}
                    </span>
                </button>
            </h3>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key="content"
                        id={id}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        <div className="px-6 pb-6 pt-2 leading-relaxed space-y-3 text-base" style={{ color: '#00203F' }}>
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default function TraficoSeresHumanos() {
    const { t } = useTranslation()
    const [openOQuee, setOpenOQuee] = useState(0)
    const [openLei, setOpenLei] = useState(0)
    const [openComoAcontece, setOpenComoAcontece] = useState(0)
    const [openVitimas, setOpenVitimas] = useState(0)

    const toggleOQuee = (i) => setOpenOQuee(openOQuee === i ? null : i)
    const toggleLei = (i) => setOpenLei(openLei === i ? null : i)
    const toggleComoAcontece = (i) => setOpenComoAcontece(openComoAcontece === i ? null : i)
    const toggleVitimas = (i) => setOpenVitimas(openVitimas === i ? null : i)

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen"
            style={PAGE_BG}
        >
            <title>{t('trafico.metaTitle')}</title>
            <meta name="description" content={t('trafico.metaDescription')} />
            {/* pageUrl() já inclui a base da língua (/, /en/, /uk/, …) */}
            <link rel="canonical" href={pageUrl('trafico-de-seres-humanos-e-exploracao')} />
            <meta property="og:url" content={pageUrl('trafico-de-seres-humanos-e-exploracao')} />
            {/* Hero */}
            <section className="py-16 lg:py-24" style={{ backgroundColor: '#00203F' }}>
                <div className="container-custom">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="font-heading text-4xl lg:text-5xl font-bold text-white leading-tight"
                    >
                        {t('trafico.h1')}
                    </motion.h1>
                    <div className="mt-6 h-1 w-24 rounded-full" style={{ backgroundColor: '#ADEFD1' }} />
                </div>
            </section>

            {/* O que é? — dark section */}
            <section className="section-padding" style={{ backgroundColor: 'rgba(0,32,63,0.88)' }}>
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h2 className="text-2xl lg:text-3xl font-bold mb-8 text-white">{t('trafico.oQueE.titulo')}</h2>
                        <div className="space-y-3">
                            <AccordionDark
                                title={t('trafico.oQueE.tsh.titulo')}
                                contexto={t('trafico.oQueE.titulo')}
                                id="oquee-0"
                                open={openOQuee === 0}
                                onToggle={() => toggleOQuee(0)}
                            >
                                <p>
                                    <Trans
                                        i18nKey="trafico.oQueE.tsh.p1"
                                        components={{
                                            a: (
                                                <a
                                                    href="https://www.citius.mj.pt/portal/article.aspx?ArticleId=578"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#ADEFD1', textDecoration: 'underline' }}
                                                />
                                            ),
                                        }}
                                    />
                                </p>
                                <p>{t('trafico.oQueE.tsh.p2')}</p>
                                <div className="grid md:grid-cols-3 gap-3 mt-3">
                                    {t('trafico.oQueE.tsh.colunas', { returnObjects: true }).map(({ label, itens }) => (
                                        <div key={label} className="rounded-lg p-4" style={{ backgroundColor: 'rgba(173,239,209,0.1)', border: '1px solid rgba(173,239,209,0.25)' }}>
                                            <p className="font-bold mb-3 text-xs uppercase tracking-wider" style={{ color: '#ADEFD1' }}>{label}</p>
                                            <ul className="space-y-1">
                                                {itens.map(item => (
                                                    <li key={item} className="text-xs text-white/80 flex gap-2">
                                                        <span style={{ color: '#ADEFD1' }} className="shrink-0">•</span>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3">
                                    <p>{t('trafico.oQueE.tsh.outrosTitulo')}</p>
                                    <ul className="mt-2 space-y-1">
                                        <li className="flex gap-2">
                                            <span style={{ color: '#ADEFD1' }} className="shrink-0">•</span>
                                            <span>
                                                <strong style={{ color: '#ADEFD1' }}>{t('trafico.oQueE.tsh.consentimentoLabel')}</strong>
                                                {t('trafico.oQueE.tsh.consentimento')}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </AccordionDark>

                            <AccordionDark
                                title={t('trafico.oQueE.exploracao.titulo')}
                                contexto={t('trafico.oQueE.titulo')}
                                id="oquee-1"
                                open={openOQuee === 1}
                                onToggle={() => toggleOQuee(1)}
                            >
                                <p>{t('trafico.oQueE.exploracao.p1')}</p>
                                <p>{t('trafico.oQueE.exploracao.p2')}</p>
                                <p>{t('trafico.oQueE.exploracao.exemploTitulo')}</p>
                                <div className="grid md:grid-cols-2 gap-3 mt-1">
                                    <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(173,239,209,0.1)', border: '1px solid rgba(173,239,209,0.25)' }}>
                                        <p className="font-bold mb-2 text-xs uppercase tracking-wider" style={{ color: '#ADEFD1' }}>{t('trafico.oQueE.exploracao.exemploExploracaoLabel')}</p>
                                        <p className="text-xs text-white/80">{t('trafico.oQueE.exploracao.exemploExploracao')}</p>
                                    </div>
                                    <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(173,239,209,0.1)', border: '1px solid rgba(173,239,209,0.25)' }}>
                                        <p className="font-bold mb-2 text-xs uppercase tracking-wider" style={{ color: '#ADEFD1' }}>{t('trafico.oQueE.exploracao.exemploTraficoLabel')}</p>
                                        <p className="text-xs text-white/80">{t('trafico.oQueE.exploracao.exemploTrafico')}</p>
                                    </div>
                                </div>
                            </AccordionDark>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* O que diz a lei em Portugal? — light section */}
            <section className="section-padding" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h2 className="text-2xl lg:text-3xl font-bold mb-8" style={{ color: '#00203F' }}>{t('trafico.lei.titulo')}</h2>
                        <div className="space-y-3">
                            <AccordionLight
                                title={t('trafico.lei.tsh.titulo')}
                                contexto={t('trafico.lei.titulo')}
                                id="lei-0"
                                open={openLei === 0}
                                onToggle={() => toggleLei(0)}
                            >
                                <p>{t('trafico.lei.tsh.p1')}</p>
                                <blockquote className="border-l-4 pl-4 italic space-y-2" style={{ borderColor: '#00203F' }}>
                                    {t('trafico.lei.tsh.citacao', { returnObjects: true }).map((para, i) => (
                                        <p key={i}>{para}</p>
                                    ))}
                                </blockquote>
                                <p>{t('trafico.lei.tsh.p2')}</p>
                                <blockquote className="border-l-4 pl-4 italic space-y-2" style={{ borderColor: '#00203F' }}>
                                    {t('trafico.lei.tsh.citacao2', { returnObjects: true }).map((para, i) => (
                                        <p key={i}>
                                            <Trans defaults={para} components={{ strong: <strong /> }} />
                                        </p>
                                    ))}
                                </blockquote>
                            </AccordionLight>

                            <AccordionLight
                                title={t('trafico.lei.exploracao.titulo')}
                                contexto={t('trafico.lei.titulo')}
                                id="lei-1"
                                open={openLei === 1}
                                onToggle={() => toggleLei(1)}
                            >
                                <p>{t('trafico.lei.exploracao.p1')}</p>
                                <p>{t('trafico.lei.exploracao.p2')}</p>
                                <ul className="space-y-1 mt-1">
                                    {t('trafico.lei.exploracao.crimes', { returnObjects: true }).map(item => (
                                        <li key={item} className="flex gap-2">
                                            <span className="shrink-0" style={{ color: '#00203F' }}>•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <p>{t('trafico.lei.exploracao.p3')}</p>
                                <ul className="space-y-1 mt-1">
                                    {t('trafico.lei.exploracao.contraordenacoes', { returnObjects: true }).map(item => (
                                        <li key={item} className="flex gap-2">
                                            <span className="shrink-0" style={{ color: '#00203F' }}>•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </AccordionLight>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Como acontece? — light section over bg */}
            <section className="section-padding" style={{ backgroundColor: 'rgba(255,255,255,0.92)' }}>
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.titulo')}</h2>
                        <p className="mb-8 text-base leading-relaxed" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.intro')}</p>
                        <div className="space-y-3">
                            <AccordionLight
                                title={t('trafico.comoAcontece.recrutamento.titulo')}
                                id="como-0"
                                open={openComoAcontece === 0}
                                onToggle={() => toggleComoAcontece(0)}
                            >
                                <p>{t('trafico.comoAcontece.recrutamento.p1')}</p>
                                <p>{t('trafico.comoAcontece.recrutamento.p2')}</p>
                            </AccordionLight>

                            <AccordionLight
                                title={t('trafico.comoAcontece.transporte.titulo')}
                                id="como-1"
                                open={openComoAcontece === 1}
                                onToggle={() => toggleComoAcontece(1)}
                            >
                                <p>{t('trafico.comoAcontece.transporte.p1')}</p>
                                <ul className="space-y-1 mt-1">
                                    {t('trafico.comoAcontece.transporte.meios', { returnObjects: true }).map(item => (
                                        <li key={item} className="flex gap-2">
                                            <span className="shrink-0" style={{ color: '#00203F' }}>•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <p>{t('trafico.comoAcontece.transporte.p2')}</p>
                            </AccordionLight>

                            <AccordionLight
                                title={t('trafico.comoAcontece.exploracao.titulo')}
                                contexto={t('trafico.comoAcontece.titulo')}
                                id="como-2"
                                open={openComoAcontece === 2}
                                onToggle={() => toggleComoAcontece(2)}
                            >
                                <p>{t('trafico.comoAcontece.exploracao.p1')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.p2')}</p>

                                <p className="font-bold mt-2" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.exploracao.laboralTitulo')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.laboralP1')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.laboralP2')}</p>
                                <ul className="space-y-1 mt-1">
                                    {t('trafico.comoAcontece.exploracao.laboralContextos', { returnObjects: true }).map(item => (
                                        <li key={item} className="flex gap-2">
                                            <span className="shrink-0" style={{ color: '#00203F' }}>•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>

                                <p className="font-bold mt-2" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.exploracao.sexualTitulo')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.sexualP1')}</p>
                                <ul className="space-y-1 mt-1">
                                    {t('trafico.comoAcontece.exploracao.sexualItens', { returnObjects: true }).map(item => (
                                        <li key={item} className="flex gap-2">
                                            <span className="shrink-0" style={{ color: '#00203F' }}>•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>

                                <p className="font-bold mt-2" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.exploracao.criminosasTitulo')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.criminosasP1')}</p>
                                <ul className="space-y-1 mt-1">
                                    {t('trafico.comoAcontece.exploracao.criminosasItens', { returnObjects: true }).map(item => (
                                        <li key={item} className="flex gap-2">
                                            <span className="shrink-0" style={{ color: '#00203F' }}>•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>

                                <p className="font-bold mt-2" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.exploracao.mendicidadeTitulo')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.mendicidadeP1')}</p>

                                <p className="font-bold mt-2" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.exploracao.casamentoTitulo')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.casamentoP1')}</p>

                                <p className="font-bold mt-2" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.exploracao.adocaoTitulo')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.adocaoP1')}</p>

                                <p className="font-bold mt-2" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.exploracao.desportoTitulo')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.desportoP1')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.desportoP2')}</p>

                                <p className="font-bold mt-2" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.exploracao.orgaosTitulo')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.orgaosP1')}</p>

                                <p className="font-bold mt-2" style={{ color: '#00203F' }}>{t('trafico.comoAcontece.exploracao.conflitosTitulo')}</p>
                                <p>{t('trafico.comoAcontece.exploracao.conflitosP1')}</p>

                                <p className="mt-2">{t('trafico.comoAcontece.exploracao.fatoresP')}</p>
                                <ul className="space-y-1 mt-1">
                                    {t('trafico.comoAcontece.exploracao.fatores', { returnObjects: true }).map(item => (
                                        <li key={item} className="flex gap-2">
                                            <span className="shrink-0" style={{ color: '#00203F' }}>•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </AccordionLight>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Quem são as vítimas? — dark section */}
            <section className="section-padding" style={{ backgroundColor: 'rgba(0,32,63,0.88)' }}>
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h2 className="text-2xl lg:text-3xl font-bold mb-6 text-white">{t('trafico.vitimas.titulo')}</h2>
                        <p className="text-white/85 leading-relaxed mb-8 text-base">{t('trafico.vitimas.intro')}</p>
                        <div className="space-y-3">
                            <AccordionDark
                                title={t('trafico.vitimas.mulheres.titulo')}
                                id="vitimas-0"
                                open={openVitimas === 0}
                                onToggle={() => toggleVitimas(0)}
                            >
                                <p>{t('trafico.vitimas.mulheres.p1')}</p>
                            </AccordionDark>

                            <AccordionDark
                                title={t('trafico.vitimas.criancas.titulo')}
                                id="vitimas-1"
                                open={openVitimas === 1}
                                onToggle={() => toggleVitimas(1)}
                            >
                                <p>{t('trafico.vitimas.criancas.p1')}</p>
                            </AccordionDark>

                            <AccordionDark
                                title={t('trafico.vitimas.migrantes.titulo')}
                                id="vitimas-2"
                                open={openVitimas === 2}
                                onToggle={() => toggleVitimas(2)}
                            >
                                <p>{t('trafico.vitimas.migrantes.p1')}</p>
                            </AccordionDark>

                            <AccordionDark
                                title={t('trafico.vitimas.outras.titulo')}
                                id="vitimas-3"
                                open={openVitimas === 3}
                                onToggle={() => toggleVitimas(3)}
                            >
                                <ul className="space-y-1">
                                    {t('trafico.vitimas.outras.itens', { returnObjects: true }).map(item => (
                                        <li key={item} className="flex gap-2">
                                            <span style={{ color: '#ADEFD1' }} className="shrink-0">•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-2">{t('trafico.vitimas.outras.p1')}</p>
                                <ul className="space-y-1 mt-1">
                                    {t('trafico.vitimas.outras.itens2', { returnObjects: true }).map(item => (
                                        <li key={item} className="flex gap-2">
                                            <span style={{ color: '#ADEFD1' }} className="shrink-0">•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </AccordionDark>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
